import DownloadIcon from "@mui/icons-material/Download";
import { Alert, Box, Button, Stack, Typography, useTheme } from "@mui/material";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { ActionNames } from "tng-address-collection-shared/actionNames.ts";
import { GlobalSearchCardsItem } from "tng-address-collection-shared/types/apiTypes";
import { useCardEditorPanel } from "../hooks/useCardEditorPanel.tsx";
import { useExportCards } from "../hooks/useExportCards.ts";
import { useExportEmails } from "../hooks/useExportEmails.ts";
import { useGlobalSearchCards } from "../hooks/useGlobalSearchCards.ts";
import { useGlobalSearchColumns } from "../hooks/useGlobalSearchColumns.ts";
import { useReloadCards } from "../hooks/useReloadCards.ts";
import { useUserHasAction } from "../hooks/useUserHasAction.ts";
import { useTranslator } from "../i18n/translator";
import { CardsTable } from "./CardsTable.tsx";
import { CardsTableToolbar } from "./CardsTableToolbar.tsx";
import { DebouncedInput } from "./DebouncedInput.tsx";
import { ErrorDisplay } from "./ErrorDisplay.tsx";

const noCards: GlobalSearchCardsItem[] = [];

export const SearchPage = () => {
  const translator = useTranslator();
  const theme = useTheme();

  const [searchTerm, setSearchTerm] = useState("");

  const { data, error, isFetching, isPending, refetch } =
    useGlobalSearchCards(searchTerm);
  const cards = data?.cards ?? noCards;

  const { handleDetailClick, selectedCardId } = useCardEditorPanel();

  const onReload = useReloadCards(refetch);

  const userHasAction = useUserHasAction();
  const canExportCards = userHasAction(ActionNames.exportCards);
  const canExportEmails = userHasAction(ActionNames.exportEmails);
  const exportCards = useExportCards();
  const exportEmails = useExportEmails();

  /* eslint-disable-next-line react-hooks/incompatible-library */
  const table = useReactTable<GlobalSearchCardsItem>({
    data: cards,
    getRowId: (row) => row.id,
    columns: useGlobalSearchColumns(handleDetailClick),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: true,
    enableSortingRemoval: false,
  });

  return (
    <Stack padding={2} spacing={2} height="100%">
      <Typography variant="h4">
        {translator("globalSearchListTitle")}
      </Typography>
      <CardsTableToolbar
        filterControls={
          <Box minWidth={theme.spacing(60)}>
            <DebouncedInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholderText={translator("searchAllCards")}
              debounce={500}
            />
          </Box>
        }
        onReload={onReload}
        isReloading={isFetching}
        resultInfo={
          <Box minWidth="max-content">
            {data?.hasMore && (
              <Alert
                severity="warning"
                sx={{ paddingTop: 0, paddingBottom: 0 }}
              >
                {translator("globalSearchMoreCardsWarning")}
              </Alert>
            )}
          </Box>
        }
        pageActions={
          (canExportCards || canExportEmails) && (
            <>
              {canExportCards && (
                <Button
                  onClick={() => exportCards.mutate()}
                  size="small"
                  variant="contained"
                  disabled={exportCards.isPending}
                  loading={exportCards.isPending}
                  sx={{ height: "100%" }}
                >
                  <DownloadIcon />
                  {translator("exportCards")}
                </Button>
              )}
              {canExportEmails && (
                <Button
                  onClick={() => exportEmails.mutate()}
                  size="small"
                  variant="contained"
                  disabled={exportEmails.isPending}
                  loading={exportEmails.isPending}
                  sx={{ height: "100%" }}
                >
                  <DownloadIcon />
                  {translator("exportEmails")}
                </Button>
              )}
            </>
          )
        }
      />
      {error ? (
        <ErrorDisplay error={error} />
      ) : searchTerm && isPending ? null : searchTerm && cards.length === 0 ? (
        <Typography color="text.secondary">
          {translator("globalSearchNoResults")}
        </Typography>
      ) : (
        <CardsTable
          table={table}
          isRowSelected={(id) => selectedCardId === id}
        />
      )}
    </Stack>
  );
};
